import { Dialog, DialogActions, DialogContent, FormHelperText, Grid } from "@mui/material";
import { LINK_LOADDATA_GUIDE } from "app/appConstants";
import { MprcButton, MprcContainer, MprcDialogTitleClose, MprcFileSelection, MprcFormSubHeader, MprcLink, MprcRequestErrorReport, MprcRequestIndicator } from "components";
import { ButtonCancel } from "components/buttons";
import { FileGetDto } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { SessionService } from "services/sessionService";

////////////////////////////////////////////////////////////////////////////////

interface IPros {
    onClose: (success: boolean) => void;
}

/////////////////////////////////////////////////////////////////////////////////

export const ImportEnrollsDialog: React.FC<IPros> = ({ onClose }) => {
    const { t } = useTranslation("lms");
    const api = useCoreApi();
    const { enqueueSnackbar } = useSnackbar();

    const [viewError, setViewError] = useState<any>();
    const [localFile, setLocalFile] = useState<FileGetDto | null>();
    const [posting, setPosting] = useState<boolean>(false);

    const success = useRef<boolean>(false);

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    const handleDownload = useCallback(() => {
        SessionService.downloadWithToken("/api/v1/EnrollmentOperation/ExportSpreadSheetImportEnrollment");
    }, []);

    const handleOpenGuide = () => {
        window.open(LINK_LOADDATA_GUIDE, "_blank");
    };

    // Ao executar
    useEffect(() => {
        async function importData() {
            try {
                await api.v1.enrollmentOperation.importEnroll({ fileId: localFile!.fileId });

                enqueueSnackbar(<div>
                    <div>{t("shared:LoadData.SuccessMessage_L1")}</div>
                    <div>{t("shared:LoadData.SuccessMessage_L2")}</div>
                </div>, { variant: "success" });
                onClose(true);
            }
            catch (e) {
                enqueueSnackbar(t("shared:LoadData.ErrorMessage"), { variant: "error" });
                setViewError(e);
            }
            finally {
                setPosting(false);
            }
        }

        if (posting) importData();
    }, [posting]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    const RenderForm = (
        <>
            <MprcContainer>
                <MprcFormSubHeader
                    title={t("shared:LoadData.ImportTitle")}
                    subtitle={
                        <>
                            {t("Enrollment.LoadData.HelperText")}
                            <br />
                            <Trans t={t} i18nKey="shared:LoadData.ImportHelperText2"
                                components={{
                                    CustomLink: <MprcLink onClick={handleOpenGuide} fontSize="inherit" />
                                }}
                            />

                        </>
                    }
                    margin="normal"
                />
                <MprcFileSelection
                    acceptedFiles={{ "": [".xlsx", ".xls", ".txt", ".csv"] }}
                    selectedFile={localFile}
                    enableClear={false}
                    mediaPreview
                    onFileUploadStarted={
                        () => {
                            setLocalFile(null);
                        }
                    }
                    onFileUploadError={() => {
                        setLocalFile(null);
                    }}
                    onFileUploadSuccess={(data) => setLocalFile(data.mainFile)}
                    onClear={() => setLocalFile(null)}
                />
            </MprcContainer>

            <MprcContainer>
                <MprcContainer>
                    <MprcLink onClick={() => handleDownload()}>
                        {t("shared:LoadData.DownloadSpreadsheetLabel")}
                    </MprcLink>
                </MprcContainer>
                <MprcContainer>
                    <FormHelperText>{t("shared:LoadData.DownloadPlainLabelHelperText1")}</FormHelperText>
                    <FormHelperText><Trans t={t} i18nKey="LoadData.DownloadPlainLabelHelperText2" /></FormHelperText>
                </MprcContainer>
            </MprcContainer>

            <MprcContainer>
                <MprcFormSubHeader title={t("shared:LoadData.ResultTitle")} />
                <FormHelperText>{t("shared:LoadData.ResultHelperText1You")}</FormHelperText>
                <FormHelperText>{t("shared:LoadData.ResultHelperText2")}</FormHelperText>
            </MprcContainer>
        </>
    );

    return (
        <Dialog open fullWidth maxWidth="sm" onClose={() => onClose(success.current)}>
            <MprcDialogTitleClose
                title={t("Enrollment.LoadData.Title")}
                onClose={() => onClose(success.current)}
                shrinkPadding
            />
            <DialogContent>
                <MprcRequestIndicator type={"post"} visible={posting} />
                {
                    viewError
                        ? <MprcRequestErrorReport error={viewError} />
                        : RenderForm
                }
            </DialogContent>
            <DialogActions>
                <Grid container justifyContent="right" spacing={2}>
                    <Grid>
                        <ButtonCancel onClick={() => onClose(false)} variant="contained" />
                    </Grid>
                    {
                        viewError == null &&
                        <Grid>
                            <MprcButton
                                fullWidth
                                disabled={localFile == null || localFile == undefined}
                                variant="contained"
                                color="primary"
                                onClick={() => setPosting(true)}
                            >
                                {t("shared:LoadData.ExecuteButtonText")}
                            </MprcButton>
                        </Grid>
                    }
                </Grid>
            </DialogActions>
        </Dialog>
    );
};
